import HomePage from '@/views/HomePage.vue';
import LoginPage from '@/views/LoginPage.vue';

import { UserRoles } from '@/_helpers/interfaces';
import i18n from '@/_helpers/plugins/i18n';
import { CreateCustomer, Customers, EditCustomer, ViewCustomer } from '@/views/Customers';
import TheDashboard from '@/views/Dashboard/TheDashboard.vue';
import HistoryLog from '@/views/HistoryLog/HistoryLog.vue';
import CreateCustomerBankAccount from '@/views/MasterData/CustomerBankAccounts/CreateCustomerBankAccount.vue';
import EditCustomerBankAccount from '@/views/MasterData/CustomerBankAccounts/EditCustomerBankAccount.vue';
import ListCustomerBankAccounts from '@/views/MasterData/CustomerBankAccounts/ListCustomerBankAccounts.vue';
import CreateSupplierBankAccount from '@/views/MasterData/SupplierBankAccounts/CreateSupplierBankAccount.vue';
import EditSupplierBankAccount from '@/views/MasterData/SupplierBankAccounts/EditSupplierBankAccount.vue';
import ListSupplierBankAccounts from '@/views/MasterData/SupplierBankAccounts/ListSupplierBankAccounts.vue';
import CreateSupplier from '@/views/MasterData/Suppliers/CreateSupplier.vue';
import EditSupplier from '@/views/MasterData/Suppliers/EditSupplier.vue';
import ListSuppliers from '@/views/MasterData/Suppliers/ListSuppliers.vue';
import MyProfile from '@/views/MyProfile/MyProfile.vue';
import NotificationCenter from '@/views/NotificationCenter/NotificationCenter.vue';
import CreateOrder from '@/views/Orders/CreateOrder/CreateOrder.vue';
import CreateRecurringInstruction from '@/views/Orders/CreateRecurringInstruction/CreateRecurringInstruction.vue';
import TheOrders from '@/views/Orders/TheOrders.vue';
import ViewOrder from '@/views/Orders/ViewOrder/ViewOrder.vue';
import ViewRecurringOrder from '@/views/Orders/ViewOrder/ViewRecurringOrder.vue';
import CreatePayment from '@/views/Payments/CreatePayment/CreatePayment.vue';
import ThePayments from '@/views/Payments/ThePayments.vue';
import ViewPayment from '@/views/Payments/ViewPayment/ViewPayment.vue';
import CreateProject from '@/views/Projects/CreateProject/CreateProject.vue';
import Projects from '@/views/Projects/Projects.vue';
import RecurringOrders from '@/views/RecurringOrders/RecurringOrders.vue';
import CreateSepaPayments from '@/views/SepaPayments/CreateSepaPayments/CreateSepaPayments.vue';
import SepaPayments from '@/views/SepaPayments/SepaPayments.vue';
import TheStatistics from '@/views/Statistics/TheStatistics.vue';
import { CreateUser, EditUser, ListUsers } from '@/views/Users';
import HijackUser from '@/views/Users/HijackUser.vue';

export const routes = [
  {
    path: '/',
    // @ts-ignore
    redirect: (i18n.global.locale as any).value,
  },
  {
    path: '/__hjk',
    name: 'hijack',
    component: HijackUser,
  },
  {
    path: '/:locale?',
    redirect: { name: 'login' },
    children: [
      {
        path: 'login',
        name: 'login',
        component: LoginPage,
      },
      {
        path: 'home',
        component: HomePage,
        children: [
          {
            path: '',
            name: 'home',
            redirect: { name: 'dashboard' },
          },
          {
            path: 'dashboard',
            name: 'dashboard',
            component: TheDashboard,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.PAYER,
                UserRoles.ORDER_CLEARER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.APPROVER,
                UserRoles.VIEWER,
              ],
              title: (i18n.global as any).t('dashboard.titles.dashboard'),
            },
          },
          {
            path: 'customers',
            name: 'customers',
            component: Customers,
            meta: {
              allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER, UserRoles.VIEWER],
              title: (i18n.global as any).t('customer_management.titles.customer_list'),
            },
          },
          {
            path: 'customers/create',
            name: 'create-customer',
            component: CreateCustomer,
            meta: {
              allowedRoles: [UserRoles.ADMIN],
              title: (i18n.global as any).t('customer_management.actions.create_customer'),
            },
          },
          {
            path: 'customers/:id',
            name: 'view-customer',
            component: ViewCustomer,
            meta: {
              allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER, UserRoles.VIEWER],
            },
          },
          {
            path: 'customers/:id/edit',
            name: 'edit-customer',
            component: EditCustomer,
            meta: {
              allowedRoles: [UserRoles.ADMIN],
            },
          },
          {
            path: 'projects',
            name: 'projects',
            component: Projects,
            meta: {
              allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER],
            },
          },
          {
            path: 'projects/create',
            name: 'create-project',
            component: CreateProject,
            meta: {
              allowedRoles: [UserRoles.ADMIN],
            },
          },
          {
            path: 'projects/:id/edit',
            name: 'edit-project',
            component: CreateProject,
            props: true,
            meta: {
              allowedRoles: [UserRoles.ADMIN],
            },
          },
          {
            path: 'statistics',
            name: 'statistics',
            component: TheStatistics,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.APPROVER,
                UserRoles.VIEWER,
                UserRoles.ORDER_CLEARER,
                UserRoles.PAYMENT_CLEARER,
              ],
            },
          },
          {
            path: 'users',
            children: [
              {
                path: '',
                name: 'list-users',
                component: ListUsers,
              },
              {
                path: 'create',
                name: 'create-user',
                component: CreateUser,
              },
              {
                path: ':userId',
                name: 'edit-user',
                component: EditUser,
                props: true,
              },
            ],
            meta: {
              allowedRoles: [UserRoles.ADMIN],
            },
          },
          {
            path: 'my-profile',
            name: 'my-profile',
            component: MyProfile,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.PAYER,
                UserRoles.ORDER_CLEARER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.APPROVER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'notification-center',
            name: 'notification-center',
            component: NotificationCenter,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.PAYER,
                UserRoles.ORDER_CLEARER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.APPROVER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'history-log',
            name: 'history-log',
            component: HistoryLog,
            meta: {
              allowedRoles: [UserRoles.ADMIN],
            },
          },
          {
            path: 'master-data',
            name: 'master-data',
            redirect: { name: 'list-suppliers' },
          },
          {
            path: 'suppliers',
            children: [
              {
                path: '',
                name: 'list-suppliers',
                component: ListSuppliers,
              },
              {
                path: 'create',
                name: 'create-supplier',
                component: CreateSupplier,
              },
              {
                path: ':supplierId',
                name: 'edit-supplier',
                component: EditSupplier,
                props: true,
              },
            ],
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.PAYER,
                UserRoles.ORDER_CLEARER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.APPROVER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'bank-accounts',
            name: 'bank-accounts',
            redirect: { name: 'list-supplier-bank-accounts' },
            children: [
              {
                path: 'supplier-accounts',
                children: [
                  {
                    path: '',
                    name: 'list-supplier-bank-accounts',
                    component: ListSupplierBankAccounts,
                  },
                  {
                    path: 'create',
                    name: 'create-supplier-bank-account',
                    component: CreateSupplierBankAccount,
                  },
                  {
                    path: ':bankAccountId',
                    name: 'edit-supplier-bank-account',
                    component: EditSupplierBankAccount,
                    props: true,
                  },
                ],
              },
              {
                path: 'customer-accounts',
                children: [
                  {
                    path: '',
                    name: 'list-customer-bank-accounts',
                    component: ListCustomerBankAccounts,
                  },
                  {
                    path: 'create',
                    name: 'create-customer-bank-account',
                    component: CreateCustomerBankAccount,
                  },
                  {
                    path: ':bankAccountId',
                    name: 'edit-customer-bank-account',
                    component: EditCustomerBankAccount,
                    props: true,
                  },
                ],
              },
            ],
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PAYER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.APPROVER,
              ],
            },
          },
          {
            path: 'sepa-payments',
            name: 'sepa-payments',
            component: SepaPayments,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PAYER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.APPROVER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'create-sepa-payments',
            name: 'create-sepa-payments',
            component: CreateSepaPayments,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PAYER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.APPROVER,
              ],
            },
          },
          {
            path: 'orders',
            name: 'orders',
            component: TheOrders,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
              title: (i18n.global as any).t('orders.titles.orders'),
            },
          },
          {
            path: 'orders/create',
            name: 'create-order',
            component: CreateOrder,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'orders/:id/edit',
            name: 'edit-order',
            component: CreateOrder,
            props: true,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'recurring-orders/create',
            name: 'create-recurring-order',
            component: CreateRecurringInstruction,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'recurring-orders/:id/edit',
            name: 'edit-recurring-order',
            component: CreateRecurringInstruction,
            props: true,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'order/:id',
            name: 'order',
            component: ViewOrder,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'recurring-orders',
            name: 'recurring-orders',
            component: RecurringOrders,
            exact: true,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
              title: (i18n.global as any).t('sidebar.items.recurring_orders'),
            },
          },
          {
            path: 'recurring-order/:id',
            name: 'recurring-order',
            component: ViewRecurringOrder,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'payments',
            name: 'payments',
            component: ThePayments,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PAYER,
                UserRoles.APPROVER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          {
            path: 'payments/create',
            name: 'create-payment',
            component: CreatePayment,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
                UserRoles.PAYER,
                UserRoles.PAYMENT_CLEARER,
              ],
            },
          },
          {
            path: 'payments/:id/edit',
            name: 'edit-payment',
            component: CreatePayment,
            props: true,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PURCHASER,
                UserRoles.APPROVER,
                UserRoles.ORDER_CLEARER,
                UserRoles.VIEWER,
                UserRoles.PAYER,
                UserRoles.PAYMENT_CLEARER,
              ],
            },
          },
          {
            path: 'payment/:id',
            name: 'payment',
            component: ViewPayment,
            meta: {
              allowedRoles: [
                UserRoles.ADMIN,
                UserRoles.PAYER,
                UserRoles.APPROVER,
                UserRoles.PAYMENT_CLEARER,
                UserRoles.VIEWER,
              ],
            },
          },
          // {
          //   path: "recurring-payments",
          //   name: "recurring-payments",
          //   component: RecurringPayments,
          //   meta: {
          //     allowedRoles: [
          //       UserRoles.ADMIN,
          //       UserRoles.PAYER,
          //       UserRoles.APPROVER,
          //       UserRoles.PAYMENT_CLEARER,
          //       UserRoles.VIEWER,
          //     ],
          //   },
          // },
        ],
      },
    ],
  },
];
